import { ref, watch } from 'vue';
import { useOrganizationStore } from '@/store/useOrganizationStore';
import { storeToRefs } from 'pinia';
import { useToast } from '@/composables/useToast';
import { useUrlValidation } from '@/composables/useUrlValidation';

/**
 * Composable for webhook settings functionality
 * Handles webhook URL management, validation, and updates
 */
export const useWebhookSettings = () => {
  const organizationStore = useOrganizationStore();
  const { preferences } = storeToRefs(organizationStore);
  const { success, error } = useToast();
  const { isValidUrl, isValidUrlRule } = useUrlValidation();

  const webhookUrl = ref('');
  const loading = ref(false);

  // Watch for changes in preferences and update local state
  watch(
    () => preferences.value?.webhookUrl,
    newUrl => {
      if (newUrl !== undefined) {
        webhookUrl.value = newUrl || '';
      }
    },
    { immediate: true }
  );

  const handleUpdateWebhook = async (data: any) => {
    // Prevent multiple simultaneous requests
    if (loading.value) {
      return;
    }

    // Extract the webhook URL from form data
    const url = data.webhook_url || webhookUrl.value;

    // Additional validation (FormKit should handle this now, but keeping as backup)
    if (url && !isValidUrl(url)) {
      error('Please enter a valid URL (e.g., https://example.com/webhook)', {
        title: 'Invalid URL Format',
      });
      return;
    }

    loading.value = true;
    try {
      await organizationStore.updateWebhookUrl(url);

      // Show success message
      success('Webhook URL has been updated successfully.', {
        title: 'Webhooks Updated',
      });
    } catch (err: any) {
      console.error('Failed to update webhook URL:', err);

      // Show user-friendly error message
      const errorMessage = err?.errors?.[0]?.detail || err?.message || 'An unexpected error occurred while updating the webhook URL.';
      error(errorMessage, {
        title: 'Update Failed',
      });
    } finally {
      loading.value = false;
    }
  };

  const initializeWebhookUrl = () => {
    if (preferences.value?.webhookUrl) {
      webhookUrl.value = preferences.value.webhookUrl;
    }
  };

  return {
    webhookUrl,
    loading,
    handleUpdateWebhook,
    initializeWebhookUrl,
    isValidUrl,
    isValidUrlRule,
  };
};
